import LoadingView from "@/components/state-handlers/LoadingView";
import AppRoutes from "@/constants/routes/AppRoutes";
import { hasAuthRedirectSearchParams } from "@/lib/oauth/isAuthCallbackSearch";
import { useAuth0 } from "@auth0/auth0-react";
import type { FC } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Root path must not SPA-redirect away from Auth0's `?code=…&state=…` callback
 * before Auth0Provider's redirect handler runs — that would strip params and lose appState.returnTo.
 * After OAuth handling, URLs have no auth params so unauthenticated visitors still go to /auth.
 *
 * Wait only while the provider is actually working on it. A callback that fails —
 * `handleRedirectCallback` rejects on a re-visited or already-exchanged code — never calls
 * `onRedirectCallback`, so the params stay in the URL: waiting on `search` alone left the user on a
 * permanent spinner while the app behind it was signed in and its queries succeeded (LAR-437).
 * Continuing to `/auth` instead drops the stale params and lets `SignInView` show the error.
 */
const IndexEntryRedirect: FC = () => {
  const { search } = useLocation();
  const { isLoading, error } = useAuth0();

  if (hasAuthRedirectSearchParams(search) && isLoading && !error) {
    return <LoadingView />;
  }

  return <Navigate to={AppRoutes.auth} replace />;
};

export default IndexEntryRedirect;
